import { useQuery } from '@tanstack/react-query';
import {
  publicOrgService,
  type PublicDirectoryEntry,
  type PublicOrganizationProfile,
} from '@/services/publicOrg';
import {
  parseBrandedSubdomainBlock,
  parseBrandedSubdomainFromApiBody,
  pathSubpathForOrgSlug,
  redirectToOrgBrandedSubdomain,
} from '@/lib/brandedSubdomainRedirect';
import { STALE_TIME } from '@/queryClient';
import { unwrapApiDataOrEmpty } from './unwrap';

const publicOrgKeys = {
  all: ['publicOrg'] as const,
  profileBySlug: (slug: string) => [...publicOrgKeys.all, 'profile', 'slug', slug] as const,
  profileBySubdomain: (label: string) =>
    [...publicOrgKeys.all, 'profile', 'subdomain', label] as const,
  nearby: (slug: string) => [...publicOrgKeys.all, 'nearby', slug] as const,
  courtHireGrid: (slug: string, date: string, sportId?: string, locationId?: string) =>
    [...publicOrgKeys.all, 'courtHire', slug, date, sportId ?? '', locationId ?? ''] as const,
};

export interface UsePublicOrgProfileOptions {
  slug?: string;
  subdomainLabel?: string;
  seed?: PublicOrganizationProfile | null;
  /** @deprecated Host resolution no longer blocks profile fetch. */
  hostLoading?: boolean;
  enabled?: boolean;
}

/** Derive shell loading state — block UI only while the first fetch is in flight. */
export function resolvePublicOrgProfileLoadingState(input: {
  canFetch: boolean;
  resolvedProfile: PublicOrganizationProfile | null;
  error: unknown;
  status: 'pending' | 'error' | 'success';
  isPending: boolean;
  isFetching: boolean;
  fetchStatus: 'fetching' | 'paused' | 'idle';
}): boolean {
  if (!input.canFetch || input.resolvedProfile || input.error) return false;
  if (input.status === 'success' || input.status === 'error') return false;
  return input.isPending && (input.isFetching || input.fetchStatus === 'fetching');
}

/** Branded subdomain hosts must use the subdomain API — path slug lookups are blocked. */
export function shouldUsePublicOrgSubdomainEndpoint(
  slug?: string,
  subdomainLabel?: string
): boolean {
  return Boolean(subdomainLabel?.trim());
}

export function usePublicOrgProfile(options: UsePublicOrgProfileOptions) {
  const { slug, subdomainLabel, seed, enabled = true } = options;

  const resolvedSlug = slug?.trim() || undefined;
  const resolvedSubdomain = subdomainLabel?.trim() || undefined;
  const useSubdomainEndpoint = shouldUsePublicOrgSubdomainEndpoint(resolvedSlug, resolvedSubdomain);

  const queryKey = useSubdomainEndpoint
    ? publicOrgKeys.profileBySubdomain(resolvedSubdomain!)
    : resolvedSlug
      ? publicOrgKeys.profileBySlug(resolvedSlug)
      : null;

  const canFetch = Boolean(enabled && (resolvedSlug || resolvedSubdomain));

  const {
    data: profile = null,
    error,
    refetch,
    isFetching,
    isPending,
    fetchStatus,
    status,
  } = useQuery({
    queryKey: queryKey ?? ['publicOrg', 'profile', 'disabled'],
    queryFn: async () => {
      try {
        const res = useSubdomainEndpoint
          ? await publicOrgService.getProfileBySubdomain(resolvedSubdomain!)
          : await publicOrgService.getProfile(resolvedSlug!);

        if (!res.success || !res.data) {
          const block = parseBrandedSubdomainFromApiBody(res);
          if (block && resolvedSlug && typeof window !== 'undefined') {
            redirectToOrgBrandedSubdomain(
              block,
              pathSubpathForOrgSlug(window.location.pathname, resolvedSlug)
            );
            throw new Error('Redirecting to branded subdomain');
          }
          throw new Error(res.error || 'Organization not found');
        }
        return res.data;
      } catch (error) {
        const block = parseBrandedSubdomainBlock(error);
        if (block && resolvedSlug && typeof window !== 'undefined') {
          redirectToOrgBrandedSubdomain(
            block,
            pathSubpathForOrgSlug(window.location.pathname, resolvedSlug)
          );
          throw new Error('Redirecting to branded subdomain');
        }
        throw error;
      }
    },
    enabled: canFetch && !!queryKey,
    staleTime: STALE_TIME.MEDIUM,
    placeholderData: seed ?? undefined,
  });

  const resolvedProfile = profile ?? seed ?? null;
  const resolvedError = error instanceof Error ? error.message : error ? String(error) : null;

  const isLoading = resolvePublicOrgProfileLoadingState({
    canFetch,
    resolvedProfile,
    error: resolvedError,
    status,
    isPending,
    isFetching,
    fetchStatus,
  });

  return {
    profile: resolvedProfile,
    isLoading,
    error: resolvedError,
    refetch,
    isFetching,
  };
}

export function usePublicOrgNearby(orgSlug: string | undefined, enabled = true) {
  const { data: nearby = [], isLoading, error } = useQuery({
    queryKey: orgSlug ? publicOrgKeys.nearby(orgSlug) : ['publicOrg', 'nearby', 'disabled'],
    queryFn: async () => {
      const res = await publicOrgService.getNearby(orgSlug!);
      return unwrapApiDataOrEmpty(res);
    },
    enabled: enabled && !!orgSlug,
    staleTime: STALE_TIME.MEDIUM,
  });

  return {
    nearby: nearby as PublicDirectoryEntry[],
    isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
  };
}

export function usePublicOrgCourtHireGrid(
  orgSlug: string | undefined,
  date: string,
  sportId?: string,
  locationId?: string,
  enabled = true
) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: orgSlug
      ? publicOrgKeys.courtHireGrid(orgSlug, date, sportId, locationId)
      : ['publicOrg', 'courtHire', 'disabled'],
    queryFn: async () => {
      const res = await publicOrgService.getCourtHireAvailabilityGrid(orgSlug!, {
        date,
        sportId,
        locationId,
      });
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to load availability');
      }
      return res.data;
    },
    enabled: enabled && !!orgSlug && !!date,
    staleTime: STALE_TIME.SHORT,
  });

  return {
    grid: data ?? null,
    isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refetch,
  };
}
